/**
 * Thumbnail extraction for the Automatic AD / Scene Description modals
 * (Electron only - uses the fluent-ffmpeg instance exposed on `window`).
 */

import { computeShotFrameTimes, frameFileName } from "./frameTimes.js";

const MAX_FRAME_WIDTH = 1024;
const CONCURRENCY = 4;

function extractOneFrame(ffmpeg, inputPath, outputPath, timeSec) {
    return new Promise((resolve, reject) => {
        ffmpeg(inputPath)
            .seekInput(timeSec)
            .outputOptions([
                "-frames:v", "1",
                // Downscale large sources (keeps LLM image tokens low); never upscale.
                "-vf", `scale='min(${MAX_FRAME_WIDTH},iw)':-2`,
                "-q:v", "4"
            ])
            .noAudio()
            .output(outputPath)
            .on("end", resolve)
            .on("error", reject)
            .run();
    });
}

/**
 * Extract one JPEG per thumbnail time for the given shots.
 *
 * @param {object} params
 * @param {Function} params.ffmpeg      fluent-ffmpeg (window.ffmpeg)
 * @param {object} params.path          Node path module (window.path)
 * @param {object} params.fs            Node fs module (window.fsSync)
 * @param {string} params.inputPath     Source media
 * @param {string} params.outputDir     Folder to write the frames into
 * @param {Array}  params.shots         Segment-relative shot changes [{time}]
 * @param {number} params.segmentStart  Segment start in the source (seconds)
 * @param {number} params.segmentDuration Segment length (seconds)
 * @param {number} params.frameRate     Project frame rate
 * @param {Function} [params.onProgress] (completed, total) => void
 * @returns {Promise<string[]>} Names of the written frame files
 */
export async function extractShotFrames({
    ffmpeg, path, fs, inputPath, outputDir, shots, segmentStart = 0, segmentDuration, frameRate, onProgress
}) {
    const times = computeShotFrameTimes(shots, segmentDuration, frameRate);
    const written = [];
    let next = 0;
    let completed = 0;

    async function worker() {
        while (next < times.length) {
            const t = times[next++];
            const name = frameFileName(t);
            const outputPath = outputDir + path.sep + name;
            try {
                await extractOneFrame(ffmpeg, inputPath, outputPath, segmentStart + t);
                if (fs.existsSync(outputPath)) written.push(name);
            } catch (err) {
                // A single unreadable frame (e.g. past the last decodable frame) is not fatal.
                console.warn(`Frame extraction failed at ${t}s:`, err);
            }
            completed++;
            if (onProgress) onProgress(completed, times.length);
        }
    }

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, times.length) }, worker));
    console.log(`Frame extraction: ${written.length} of ${times.length} thumbnails written`);
    return written.sort();
}
